import { createAsyncThunk } from '@reduxjs/toolkit';
import payrollServiceApiInstance from '../../../../../config/payrollApiConfig';
import dayjs from 'dayjs';

// Create
export const createTdsDeclaration = createAsyncThunk(
    'tdsDeclaration/create',
    async (declarationData: any, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            declarationData.FinancialYearID !== undefined && formData.append("FinancialYearID", declarationData.FinancialYearID);
            declarationData.SectionCode !== undefined && formData.append("SectionCode", declarationData.SectionCode);
            declarationData.SubType !== undefined && formData.append("SubType", declarationData.SubType);
            declarationData.DeclaredAmount !== undefined && formData.append("DeclaredAmount", declarationData.DeclaredAmount);
            declarationData.ProofSubmittedAmount !== undefined && formData.append("ProofSubmittedAmount", declarationData.ProofSubmittedAmount);
            declarationData.VerifiedAmount !== undefined && formData.append("VerifiedAmount", declarationData.VerifiedAmount);
            declarationData.ProofStatus !== undefined && formData.append("ProofStatus", declarationData.ProofStatus);
            declarationData.VerificationStatus !== undefined && formData.append("VerificationStatus", declarationData.VerificationStatus);

            if (declarationData?.DeclarationDate) {
                formData.append("DeclarationDate", dayjs(declarationData.DeclarationDate).format("YYYY-MM-DD"));
            }

            declarationData.FromMonth !== undefined && formData.append("FromMonth", declarationData.FromMonth);
            declarationData.ToMonth !== undefined && formData.append("ToMonth", declarationData.ToMonth);
            declarationData.DocumentRef !== undefined && formData.append("DocumentRef", declarationData.DocumentRef);
            declarationData.CreatedBy !== undefined && formData.append("CreatedBy", declarationData.CreatedBy);
            declarationData.ModifiedBy !== undefined && formData.append("ModifiedBy", declarationData.ModifiedBy);
            declarationData.EmployeeID !== undefined && formData.append("EmployeeID", declarationData.EmployeeID);
            declarationData.Description !== undefined && formData.append("Description", declarationData.Description);

            if (declarationData.file) {
                formData.append("file", declarationData.file);
            }

            const response = await payrollServiceApiInstance.post("/tds-declaration", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || "Failed to create TDS declaration");
        }
    }
);

// Update
export const updateTdsDeclaration = createAsyncThunk(
    'tdsDeclaration/update',
    async ({ id, ...declarationData }: any & { id: number }, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            declarationData.FinancialYearID !== undefined && formData.append("FinancialYearID", declarationData.FinancialYearID);
            declarationData.SectionCode !== undefined && formData.append("SectionCode", declarationData.SectionCode);
            declarationData.SubType !== undefined && formData.append("SubType", declarationData.SubType);
            declarationData.DeclaredAmount !== undefined && formData.append("DeclaredAmount", declarationData.DeclaredAmount);
            declarationData.ProofSubmittedAmount !== undefined && formData.append("ProofSubmittedAmount", declarationData.ProofSubmittedAmount);
            declarationData.VerifiedAmount !== undefined && formData.append("VerifiedAmount", declarationData.VerifiedAmount);
            declarationData.ProofStatus !== undefined && formData.append("ProofStatus", declarationData.ProofStatus);
            declarationData.VerificationStatus !== undefined && formData.append("VerificationStatus", declarationData.VerificationStatus);

            if (declarationData?.DeclarationDate) {
                formData.append("DeclarationDate", dayjs(declarationData.DeclarationDate).format("YYYY-MM-DD"));
            }

            declarationData.FromMonth !== undefined && formData.append("FromMonth", declarationData.FromMonth);
            declarationData.ToMonth !== undefined && formData.append("ToMonth", declarationData.ToMonth);
            declarationData.DocumentRef !== undefined && formData.append("DocumentRef", declarationData.DocumentRef);
            declarationData.ModifiedBy !== undefined && formData.append("ModifiedBy", declarationData.ModifiedBy);
            declarationData.EmployeeID !== undefined && formData.append("EmployeeID", declarationData.EmployeeID);
            declarationData.Description !== undefined && formData.append("Description", declarationData.Description);

            // Append file if it exists
            if (declarationData.file) {
                console.log("Appending file:", declarationData.file);
                formData.append("file", declarationData.file);
            }

            // Make the PUT request with the form data
            const response = await payrollServiceApiInstance.put(
                `/tds-declaration/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || "Failed to update TDS declaration");
        }
    }
);

// Fetch All
export const fetchTdsDeclarations = createAsyncThunk(
    'tdsDeclaration/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response: any = await payrollServiceApiInstance.get('/tds-declaration');
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to fetch TDS declarations');
        }
    }
);

// Fetch by ID
export const fetchTdsDeclarationById = createAsyncThunk(
    'tdsDeclaration/fetchById',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await payrollServiceApiInstance.get(`/tds-declaration/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to fetch TDS declaration');
        }
    }
);

// Delete
export const deleteTdsDeclaration = createAsyncThunk(
    'tdsDeclaration/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await payrollServiceApiInstance.delete(`/tds-declaration/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to delete TDS declaration');
        }
    }
);

// Upload Documents for Declaration
export const uploadTdsDeclarationDocuments = createAsyncThunk(
    'tdsDeclaration/uploadDocuments',
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await payrollServiceApiInstance.post('/tds-declaration/upload', data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to upload TDS declaration documents');
        }
    }
);

// Get Document by Declaration ID
export const fetchTdsDeclarationDocumentById = createAsyncThunk(
    'tdsDeclaration/fetchDocumentById',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await payrollServiceApiInstance.get(`/tds-declaration/${id}/document`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to fetch TDS declaration document');
        }
    }
);

//Get TDS Declarations by Employee ID
export const fetchTdsDeclarationsByEmployeeId = createAsyncThunk(
    'tdsDeclaration/fetchByEmployeeId',
    async (employeeId: number, { rejectWithValue }) => {
        try {
            const response = await payrollServiceApiInstance.get(`/tds-declaration/employee/${employeeId}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to fetch TDS declarations by employee ID');
        }
    }
);
